// What the phrase slice player is doing, readable without importing the
// playback service.
//
// A phrase slice plays one span of a tutor clip on the playback service's
// second player (src/scripts/audioPlaybackService.js, "Phrase slices"). The
// service owns the player and writes this state. It lives in its own module,
// importing nothing but Vue, because several component suites mock the whole
// playback service without its newer exports (vitest refuses a missing export
// of a mocked module), and TutorMessage and ChatScreen must still be able to
// stop a slice when they tear down. With the service mocked, nothing registers
// a controller and stopping is a no-op.

import { reactive } from "vue";

// status is "idle", "loading" (the tap was accepted and the bytes or the
// player are not ready yet) or "playing". owner is whatever the caller passed
// to playSlice to identify the phrase, normally {dialogId, messageKey,
// spanIndex}, so a message can tint its own phrase and stop only its own
// slice. loop is whether the slice repeats until it is stopped. taps counts
// every phrase tap the service accepted: a pill that was asked to play and is
// still downloading its clip records the count when it was asked, and gives
// up the play if a phrase was tapped since, because a pill load still pending
// at a phrase tap must never start afterwards.
export const sliceState = reactive({
  status: "idle",
  audioId: null,
  start: 0,
  end: 0,
  loop: false,
  owner: null,
  taps: 0,
});

let controller = null;

/** Called once by the playback service when it loads. */
export function registerSliceController(nextController) {
  controller = nextController || null;
}

/**
 * Stops the slice, and any loop, whose owner matches every key of `filter`
 * ({dialogId, messageKey}), or any slice at all when no filter is given.
 * TutorMessage calls it when it unmounts and ChatScreen when it resets the
 * chat, because a loop must never outlive the button that stops it.
 */
export function stopSlicesFor(filter = null) {
  if (controller) controller.stopSlicesFor(filter);
}

/**
 * Plays one phrase slice through the playback service's playSlice. Must be
 * called synchronously from the tap's handler, like playSlice itself, so the
 * slice element is primed inside the tap. Resolves false when no service has
 * registered (a component suite that mocks the service).
 * @param {{audioId: string, start: number, end: number, loop?: boolean,
 *   owner?: object}} request
 * @returns {Promise<boolean>}
 */
export function playPhraseSlice(request) {
  if (!controller || typeof controller.playSlice !== "function") return Promise.resolve(false);
  return controller.playSlice(request);
}

/** Turns the loop on or off for the slice that is sounding or loading. */
export function setPhraseSliceLoop(loop) {
  if (controller && typeof controller.setSliceLoop === "function") controller.setSliceLoop(loop);
}

/**
 * Whether any reply is being spoken as segments right now (its segment phase
 * is "playing" or "waiting"). A phrase slice, the pill's replay button and a
 * snapped scrub release all pause or replace the shared player, which ends a
 * live segment session and cuts the rest of that reply short, so they stay
 * inert while this is true. Reactive when read inside a computed, because it
 * reads the service's reactive phase map.
 * @returns {boolean}
 */
export function anySegmentSessionLive() {
  if (!controller || typeof controller.anySegmentSessionLive !== "function") return false;
  return !!controller.anySegmentSessionLive();
}

/**
 * Cancels every main-player play() of `audioId` that has not started sounding
 * yet, and nothing else: a clip already sounding and every other clip's load
 * are left alone. AudioPlayer calls it when it unmounts, so a pill that is
 * gone never starts its clip afterwards.
 * @param {string} audioId
 */
export function cancelPendingPlaysOf(audioId) {
  if (controller && typeof controller.cancelPendingPlaysOf === "function") {
    controller.cancelPendingPlaysOf(audioId);
  }
}

/**
 * Cancels every main-player play() of a clip other than `audioId` that has
 * not reached the iOS plugin yet (on the web, that has not started sounding).
 * AudioPlayer calls it for a live action on the clip the player holds (a seek
 * by span, a step, a drag, a replay), which calls no play() of its own and so
 * would not otherwise supersede an older start of another clip. A load
 * already crossing the bridge to the plugin is left alone: the plugin
 * installs it whatever JavaScript decides, and pausing it then would leave
 * nothing playing.
 * @param {string} audioId the clip the learner just acted on
 */
export function supersedeOtherPendingPlays(audioId) {
  if (controller && typeof controller.supersedeOtherPendingPlays === "function") {
    controller.supersedeOtherPendingPlays(audioId);
  }
}
